import { KeyboardEvent, MouseEvent, useCallback } from 'react'
import cn from 'classnames'
import tableCellStyles from '../../TableCell/TableCell.module.scss'
import styles from './CellAttributionMarker.module.scss'

interface IProps {
  /** Pending suggestions awaiting review. This marker stands for nothing else. */
  suggestedCount: number
  /**
   * - `ring`   standalone in a cell corner: the cell has attribution but no note of its own.
   * - `accent` beside an existing note marker, so a note cannot hide pending review.
   * - `inline` in a table header, for facility-wide facts that belong to no single cell.
   */
  variant?: 'ring' | 'accent' | 'inline'
  onClick: () => void
}

/**
 * The review signal in a cell's top-right corner.
 *
 * Standalone it is a hollow dashed ring — deliberately unlike the solid note dog-ear, so
 * "someone wrote this" and "Arc proposes this" never read as the same thing. Once a
 * suggestion is approved the count drops and the ring gives way to the solid marker.
 *
 * It stands for pending review and nothing else. Established facts are read, not decided,
 * so they live in the notes drawer and are counted by the solid marker instead.
 *
 * Clicks are stopped here so the cell underneath keeps its normal behaviour: left-click
 * still selects, right-click still opens copy / add-note.
 */
const CellAttributionMarker = ({ suggestedCount, variant = 'ring', onClick }: IProps) => {
  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onClick()
    },
    [onClick],
  )

  // Keyboard parity with the mouse affordance. Enter/Space are stopped as well as handled,
  // so activating the marker never also reaches the cell's own key handling.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      event.stopPropagation()
      onClick()
    },
    [onClick],
  )

  if (suggestedCount === 0) return null

  // The accent tracks the note marker as it changes shape on hover, so its coordinates live
  // with that marker's styles rather than here. The other variants own their corner outright.
  const variantClass = variant === 'accent' ? tableCellStyles.accent : styles[variant]

  const title = `${suggestedCount} suggestion${suggestedCount > 1 ? 's' : ''} to review`

  return (
    <div
      className={cn(styles.root, variantClass)}
      title={title}
      aria-label={title}
      role="button"
      tabIndex={0}
      data-cy="cell-attribution-marker"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  )
}

export default CellAttributionMarker
